import { useState, FormEvent } from 'react';

interface InquiryFormProps {
  formId?: string;
  formUrl?: string;
  sectionId?: string;
}

const ZAPIER_WEBHOOK = 'https://hooks.zapier.com/hooks/catch/26282998/un33dif/';

export default function InquiryForm({
  formId = 'inquiry-form',
  formUrl = 'https://readdy.ai/api/form/d6b94k6a728k8ctu3p4g',
  sectionId = 'inquiry',
}: InquiryFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const zapierBody = new URLSearchParams();
      zapierBody.append('full_name', formData.name);
      zapierBody.append('email', formData.email);
      zapierBody.append('phone', formData.phone);
      zapierBody.append('company_firm', formData.company);
      zapierBody.append('project_type', formData.projectType);
      zapierBody.append('timeline', formData.timeline);
      zapierBody.append('product_details', formData.message);

      const [response] = await Promise.all([
        fetch(formUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(ZAPIER_WEBHOOK, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: zapierBody.toString(),
        }).catch(() => null),
      ]);

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', projectType: '', timeline: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={sectionId} className="py-24 bg-[#F5F0EB]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-serif font-bold text-[#2B2B2B] mb-4">
            Start Your Inquiry
          </h2>
          <p className="text-gray-600 text-lg">
            Tell us about your project and we&apos;ll get back to you within 24 hours
          </p>
        </div>

        <div className="bg-white rounded-3xl p-10">
          <form id={formId} onSubmit={handleSubmit} data-readdy-form>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor={`${formId}-name`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Full Name <span className="text-[#C5A572]">*</span>
                </label>
                <input
                  type="text"
                  id={`${formId}-name`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Email Address <span className="text-[#C5A572]">*</span>
                </label>
                <input
                  type="email"
                  id={`${formId}-email`}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Phone Number <span className="text-[#C5A572]">*</span>
                </label>
                <input
                  type="tel"
                  id={`${formId}-phone`}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label htmlFor={`${formId}-company`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Company / Firm Name
                </label>
                <input
                  type="text"
                  id={`${formId}-company`}
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Your design firm"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor={`${formId}-projectType`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Project Type
                </label>
                <select
                  id={`${formId}-projectType`}
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="retail">Retail</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor={`${formId}-timeline`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                  Project Timeline
                </label>
                <select
                  id={`${formId}-timeline`}
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate (1-2 weeks)</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="1-3-months">1-3 months</option>
                  <option value="3-6-months">3-6 months</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor={`${formId}-message`} className="block text-sm font-medium text-[#2B2B2B] mb-2">
                Project Details
              </label>
              <textarea
                id={`${formId}-message`}
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C5A572] focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your project needs, specific products you&apos;re looking for, or any questions you have..."
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                <i className="ri-checkbox-circle-line mr-2"></i>
                Thank you! Your inquiry has been submitted. We&apos;ll be in touch within 24 hours.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                <i className="ri-error-warning-line mr-2"></i>
                Please fill in all required fields (name, email &amp; phone).
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#2B2B2B] text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-[#C5A572] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
